import React from 'react';
import { motion } from 'framer-motion';
import { Pen, Palette, Sparkles, Star } from 'lucide-react';

// Floating decorative elements
const FloatingDecorations = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`
          }}
          animate={{
            y: [-10, 10, -10],
            x: [-5, 5, -5],
            rotate: [0, 360]
          }}
          transition={{
            duration: 5 + Math.random() * 5,
            delay: Math.random() * 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }}
        >
          <Star
            size={8 + Math.random() * 8}
            className="text-amber-400/20"
            fill="currentColor"
          />
        </motion.div>
      ))}
    </div>
  );
};

// Team member card component
const TeamCard = ({ member, index, isReversed }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.2, duration: 0.8 }}
      className="relative mb-24 last:mb-0"
    >
      {/* Background decorative circles */}
      <motion.div
        className={`absolute w-32 h-32 rounded-full -z-10 ${isReversed ? '-right-8 -top-8' : '-left-8 -top-8'}`}
        style={{
          background: member.accentGradient,
          filter: 'blur(40px)',
          opacity: 0.3
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.2, 0.4, 0.2]
        }}
        transition={{ duration: 5, repeat: Infinity }}
      />

      {/* Main card */}
      <div className="relative">
        {/* Gradient border */}
        <div
          className="absolute inset-0 rounded-3xl p-[2px]"
          style={{ background: member.borderGradient }}
        >
          <div className="w-full h-full bg-[#0f0f2a] rounded-3xl" />
        </div>

        {/* Content */}
        <div className={`relative grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-3xl overflow-hidden ${isReversed ? 'lg:flex-row-reverse' : ''}`}>
          {/* Image column */}
          <motion.div
            className={`lg:col-span-5 relative ${isReversed ? 'lg:order-2' : 'lg:order-1'}`}
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.4 }}
          >
            <div className="h-80 lg:h-full min-h-[400px] relative overflow-hidden">
              <img
                src={member.image}
                alt={member.name}
                className="absolute inset-0 w-full h-full object-cover"
              />

              {/* Gradient overlay */}
              <div
                className={`absolute inset-0 ${isReversed ? 'bg-gradient-to-l' : 'bg-gradient-to-r'}`}
                style={{
                  background: `linear-gradient(${isReversed ? '90deg' : '-90deg'}, transparent 0%, ${member.overlayColor} 100%)`
                }}
              />

              {/* Bottom fade */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f2a] via-transparent to-transparent lg:hidden" />

              {/* Role badge */}
              <motion.div
                className="absolute top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-full"
                style={{ background: member.accentGradient }}
                whileHover={{ scale: 1.05 }}
              >
                {member.icon}
                <span className="text-sm font-bold text-white">{member.role}</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Text column */}
          <div className={`lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center ${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
            <motion.div
              initial={{ opacity: 0, x: isReversed ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              {/* Title */}
              <div className="flex items-center gap-3 mb-4">
                <span className="text-gray-400 text-lg">Meet the</span>
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>

              <h2 className="font-display text-4xl lg:text-5xl font-bold mb-6">
                <span className="text-white">{member.title} </span>
                <span
                  className="relative inline-block"
                  style={{
                    background: member.nameGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}
                >
                  {member.name}
                  <motion.div
                    className="absolute -bottom-2 left-0 h-2 rounded-full -z-10"
                    style={{ background: member.underlineColor }}
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  />
                </span>
              </h2>

              {/* Description */}
              <div className="space-y-4 text-gray-300 leading-relaxed">
                {member.paragraphs.map((paragraph, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + i * 0.1 }}
                    className="font-serif"
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </div>

              {/* Decorative quote */}
              <motion.div
                className="mt-8 p-4 rounded-xl bg-white/5 border-l-4"
                style={{ borderColor: member.accentColor }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
              >
                <p className="text-gray-400 italic font-serif">
                  "{member.quote}"
                </p>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const TeamSection = () => {
  const team = [
    {
      title: 'Author',
      name: 'Carter Case',
      role: 'AUTHOR',
      image: '/images/carter1.jpg',
      icon: <Pen className="w-4 h-4" />,
      borderGradient: 'linear-gradient(135deg, #f59e0b, #fcd34d, #f59e0b)',
      accentGradient: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)',
      nameGradient: 'linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)',
      underlineColor: 'rgba(245, 158, 11, 0.3)',
      accentColor: '#f59e0b',
      overlayColor: 'rgba(15, 15, 42, 0.4)',
      paragraphs: [
        "Meet the creative mind behind the delightful tales of \"Ned and Nancy\" – Carter Case. With a passion for storytelling and a knack for crafting humorous and mysterious adventures, Carter has captivated the hearts of young readers and their families.",
        "In his mid-30s, Carter Case brings a fresh perspective to the world of children's literature. Drawing inspiration from his own childhood adventures and a vivid imagination, he weaves engaging narratives that resonate with both kids and adults alike."
      ],
      quote: "Every mystery begins with a curious mind and an open heart."
    },
    {
      title: 'Illustrator',
      name: 'Rasa',
      role: 'ILLUSTRATOR',
      image: '/images/rasa.jpg',
      icon: <Palette className="w-4 h-4" />,
      borderGradient: 'linear-gradient(135deg, #a855f7, #ec4899, #a855f7)',
      accentGradient: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
      nameGradient: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
      underlineColor: 'rgba(168, 85, 247, 0.3)',
      accentColor: '#a855f7',
      overlayColor: 'rgba(15, 15, 42, 0.4)',
      paragraphs: [
        "Rasa's illustrations are celebrated for their ability to capture the imagination of both young and old readers. Her work on the Ned and Nancy series has been instrumental in bringing their adventures to life through vibrant and engaging artwork.",
        "With a distinctive style that blends whimsy and detail, Rasa creates illustrations that perfectly complement the stories' mysterious and humorous nature. Each page becomes a visual journey that invites readers to explore and discover hidden details."
      ],
      quote: "Art is the magic that turns words into worlds."
    }
  ];

  return (
    <div className="relative py-24 lg:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3a 30%, #0f0f2a 70%, #0a0a1a 100%)'
          }}
        />

        {/* Radial glows */}
        <motion.div
          className="absolute top-1/4 left-0 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
          animate={{
            x: [-50, 50, -50],
            scale: [1, 1.2, 1]
          }}
          transition={{ duration: 12, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-0 w-[500px] h-[500px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 60%)',
            filter: 'blur(80px)'
          }}
          animate={{
            x: [50, -50, 50],
            scale: [1.2, 1, 1.2]
          }}
          transition={{ duration: 12, repeat: Infinity }}
        />

        <FloatingDecorations />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-[1px] w-16 bg-gradient-to-r from-transparent to-amber-500" />
            <div className="flex gap-2">
              <Pen className="w-6 h-6 text-amber-400" />
              <Palette className="w-6 h-6 text-purple-400" />
            </div>
            <div className="h-[1px] w-16 bg-gradient-to-l from-transparent to-purple-500" />
          </div>

          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">The </span>
            <span className="gradient-text-gold">Creative Minds</span>
          </h2>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto font-serif italic">
            Meet the talented duo bringing Ned and Nancy's adventures to life
          </p>
        </motion.div>

        {/* Team cards */}
        {team.map((member, index) => (
          <TeamCard
            key={member.name}
            member={member}
            index={index}
            isReversed={index % 2 === 1}
          />
        ))}
      </div>
    </div>
  );
};

export default TeamSection;
